/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Chip,
  Tooltip,
  Collapse,
  Button,
} from '@mui/material';
import {
  History as HistoryIcon,
  AddBox as AddBoxIcon,
  Send as SendIcon,
  SmartToy as SmartToyIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  ThumbUpAlt as ThumbUpAltIcon,
  ThumbUpAltOutlined as ThumbUpAltOutlinedIcon,
  ThumbDownAlt as ThumbDownAltIcon,
  ThumbDownAltOutlined as ThumbDownAltOutlinedIcon,
} from '@mui/icons-material';
import { apiUrl } from '../../utilities/apiUrl';
import { readAssistantStream } from '../../utilities/assistantStream';
import { EditTextModal } from '../Modal/EditTextModal';

// Placeholder prompts
const SUGGESTED_PROMPTS = [
  'What are the core hours?',
  'What is the earliest start time?',
  'How do I compute the hours for the overtime application?',
  'What are the HMO benefits and coverage? How do I enroll dependents in our HMO benefits? How do I submit medical or reimbursement claims?',
  'How to contact HR?',
];

const headerIconButtonSx = { color: '#84D8C5' };

const messageRowSx = (sender) => ({
  display: 'flex',
  justifyContent: sender === 'user' ? 'flex-end' : 'flex-start',
});

const messageBubbleSx = (sender) => ({
  maxWidth: '80%',
  px: 1.5,
  py: 1,
  borderRadius: '12px',
  bgcolor: sender === 'user' ? '#84D8C5' : 'rgba(255,255,255,0.08)',
  color: sender === 'user' ? '#1A202C' : '#FFFFFF',
});

const feedbackRowSx = {
  mt: 0.75,
  pt: 0.75,
  borderTop: '1px solid rgba(255, 255, 255, 0.12)',
};

const feedbackIconSx = (selected) => ({
  color: selected ? '#84D8C5' : 'rgba(255, 255, 255, 0.5)',
  '&:hover': { color: '#84D8C5' },
});

const feedbackNoteSx = (isError) => ({
  color: isError ? 'error.light' : 'rgba(255, 255, 255, 0.6)',
});

const promptChipSx = {
  position: 'relative',
  justifyContent: 'flex-start',
  height: 'auto',
  py: 0.75,
  color: '#FFFFFF',
  border: '1.5px solid rgba(255, 255, 255, 0.24)',
  transition: 'border-color 150ms ease',
  '&:hover': { borderColor: 'transparent' },
  // Gradient ring
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: 'inherit',
    padding: '1.5px',
    background:
      'radial-gradient(120px circle at var(--x, 50%) var(--y, 50%), #84D8C5, rgba(255, 255, 255, 0.2) 60%, transparent 80%)',
    WebkitMask:
      'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    opacity: 0,
    transition: 'opacity 150ms ease',
    pointerEvents: 'none',
  },
  '&:hover::before': { opacity: 1 },
  '& .MuiChip-label': { whiteSpace: 'normal' },
  flexShrink: 0,
};

// Same style as prompt chips
const feedbackCommentChipSx = {
  position: 'relative',
  color: '#FFFFFF',
  fontSize: '0.7rem',
  border: '1.5px solid rgba(255, 255, 255, 0.24)',
  transition: 'border-color 150ms ease',
  '&:hover': { borderColor: 'transparent' },
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: 'inherit',
    padding: '1.5px',
    background:
      'radial-gradient(60px circle at var(--x, 50%) var(--y, 50%), #84D8C5, rgba(255, 255, 255, 0.2) 60%, transparent 80%)',
    WebkitMask:
      'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    opacity: 0,
    transition: 'opacity 150ms ease',
    pointerEvents: 'none',
  },
  '&:hover::before': { opacity: 1 },
  flexShrink: 0,
};

const sendButtonSx = (hasInput) => ({
  color: hasInput ? '#84D8C5' : 'rgba(255, 255, 255, 0.3)',
});

const typingDotSx = (delay) => ({
  width: 6,
  height: 6,
  borderRadius: '50%',
  bgcolor: 'currentColor',
  animation: 'eps-typing-bounce 1.2s infinite ease-in-out',
  animationDelay: `${delay}s`,
  '@keyframes eps-typing-bounce': {
    '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.35 },
    '40%': { transform: 'scale(1)', opacity: 1 },
  },
});

// Renders the three-dot typing indicator for the assistant.
const TypingIndicator = () => (
  <Tooltip title="Assistant is thinking...">
    <Box
      aria-label="Assistant is thinking"
      sx={{ display: 'flex', gap: 0.5, alignItems: 'center', py: 0.5 }}
    >
      <Box sx={typingDotSx(0)} />
      <Box sx={typingDotSx(0.15)} />
      <Box sx={typingDotSx(0.3)} />
    </Box>
  </Tooltip>
);

// A 401 means the session expired, ask user to log in again.
const SESSION_EXPIRED_MESSAGE =
  'Your session has expired. Please refresh the page and log in again.';
const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';
const FEEDBACK_SESSION_EXPIRED_MESSAGE =
  'Your session has expired. Refresh the page to send feedback.';
const FEEDBACK_ERROR_MESSAGE = "Couldn't send feedback. Please try again.";
const FEEDBACK_SAVED_MESSAGE = 'Thanks for your feedback.';
// Matches the backend's FeedbackIn.comment max_length.
const FEEDBACK_COMMENT_MAX_LENGTH = 1000;

// Marks a message as failed, setting its status to 'error' and providing a fallback text if the message has no text.
const markFailed = (msg, fallbackText = GENERIC_ERROR_MESSAGE) => ({
  ...msg,
  status: 'error',
  text: msg.text || fallbackText,
});

// Remove duplicates for source titles in citations.
const uniqueSourceTitles = (citations = []) => [
  ...new Set(citations.map((citation) => citation.document_title)),
];

// Marks the maximum length for auto-generated conversation titles.
const AUTO_TITLE_LENGTH = 60;

const autoTitleFrom = (text) => {
  const collapsed = text.trim().replace(/\s+/g, ' ');
  if (collapsed.length <= AUTO_TITLE_LENGTH) return collapsed;
  return `${collapsed.slice(0, AUTO_TITLE_LENGTH - 1).trimEnd()}...`;
};

const TITLE_WORD_LIMIT = 15;

const truncateTitle = (text, limit = TITLE_WORD_LIMIT) => {
  const words = text.trim().split(/\s+/);
  if (words.length <= limit) return text;
  return `${words.slice(0, limit).join(' ')}...`;
};

const PROMPT_SCROLL_AMOUNT = 240;
// Side panel's collapsed prompt grid: 2 rows of 2.
const PROMPT_COLLAPSED_COUNT = 4;

// Scroll arrow buttons
const promptScrollArrowSx = {
  flexShrink: 0,
  bgcolor: 'rgba(255, 255, 255, 0.08)',
  color: '#84D8C5',
  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.16)' },
  '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.2)' },
};

// Rotate one chevron when expanded for a smooth animation.
const promptToggleIconSx = (expanded) => ({
  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 200ms ease',
});

export const ChatPanel = ({
  variant = 'sidePanel',
  onNewChat,
  onHistoryClick,
  onConversationStarted,
  onConversationRenamed,
  initialTitle = 'New Chat',
  conversationId = null,
}) => {
  const navigate = useNavigate();
  const [title, setTitle] = useState(initialTitle);
  const [messages, setMessages] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(!!conversationId);
  const [isSending, setIsSending] = useState(false);
  const [input, setInput] = useState('');
  const [isInputMultiline, setIsInputMultiline] = useState(false);
  const [canScrollPromptsLeft, setCanScrollPromptsLeft] = useState(false);
  const [canScrollPromptsRight, setCanScrollPromptsRight] = useState(false);
  const [promptsExpanded, setPromptsExpanded] = useState(false);
  // Tracks the active conversation ID for rendering and renaming purposes.
  const [activeConversationId, setActiveConversationId] =
    useState(conversationId);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [renameOpenCount, setRenameOpenCount] = useState(0);
  const chatBoxRef = useRef(null);
  const textareaRef = useRef(null);
  const promptRowRef = useRef(null);
  const messageIdRef = useRef(0);
  const conversationIdRef = useRef(null);

  // Stops double sending a message while assistant is thinking
  // Uses ref instead of state to avoid race conditions with rapid Enter
  const isSendingRef = useRef(false);
  const nextMessageId = () => {
    messageIdRef.current += 1;
    return messageIdRef.current;
  };

  // Applies an update to one message bubble by its local id. `updater` takes
  // the current message and returns the next one, so callers can build on
  // fields (e.g. appending streamed token text) without racing prior sets.
  const patchMessage = (id, updater) => {
    setMessages((prev) =>
      prev.map((msg) => (msg.id === id ? updater(msg) : msg))
    );
  };

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [messages]);

  // Feedback handlers: rating selection, comment box, and submission. Feedback is sent to the backend for storage, but not used to train the model.
  const handleSelectRating = (msg, rating) => {
    patchMessage(msg.id, (m) => ({
      ...m,
      feedback: rating,
      feedbackDraftOpen: true,
      feedbackDraft: m.feedbackDraft ?? m.feedbackComment ?? '',
      feedbackStatus: 'idle',
    }));
  };

  const handleOpenFeedbackComment = (msg) => {
    patchMessage(msg.id, (m) => ({
      ...m,
      feedbackDraftOpen: true,
      feedbackDraft: m.feedbackDraft ?? m.feedbackComment ?? '',
    }));
  };

  const handleFeedbackDraftChange = (msgId, value) => {
    patchMessage(msgId, (m) => ({ ...m, feedbackDraft: value }));
  };

  const handleSubmitFeedback = async (msg) => {
    if (!msg.feedback || !msg.messageId || msg.feedbackStatus === 'saving')
      return;

    patchMessage(msg.id, (m) => ({ ...m, feedbackStatus: 'saving' }));
    const comment = (msg.feedbackDraft ?? '').trim();

    try {
      const response = await fetch(
        apiUrl(`/assistant/messages/${msg.messageId}/feedback`),
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            rating: msg.feedback,
            comment: comment || null,
          }),
        }
      );

      if (response.status === 401) {
        patchMessage(msg.id, (m) => ({ ...m, feedbackStatus: 'unauthorized' }));
        return;
      }
      if (!response.ok) {
        throw new Error(
          `Feedback request failed with status ${response.status}`
        );
      }

      patchMessage(msg.id, (m) => ({
        ...m,
        feedbackComment: comment,
        feedbackDraftOpen: false,
        feedbackStatus: 'saved',
      }));
    } catch (err) {
      console.log(err);
      patchMessage(msg.id, (m) => ({ ...m, feedbackStatus: 'error' }));
    }
  };

  // Load history once when this component opens. It remounts for each session,
  // so conversation ID doesn't need to be watched.
  useEffect(() => {
    if (!conversationId) return;

    conversationIdRef.current = conversationId;

    fetch(apiUrl(`/assistant/conversations/${conversationId}`), {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => {
        if (response.status === 401) {
          setMessages([
            {
              id: nextMessageId(),
              sender: 'assistant',
              text: SESSION_EXPIRED_MESSAGE,
              citations: [],
              status: 'error',
            },
          ]);
          return null;
        }
        if (!response.ok)
          throw new Error(`Failed to load conversation: ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (!data) return; // 401

        // Determine if the conversation title was auto-generated or renamed by the user.
        const firstUserMessage = (data.messages ?? []).find(
          (item) => item.role === 'user'
        );
        const wasRenamed =
          !!firstUserMessage &&
          data.title !== autoTitleFrom(firstUserMessage.content);
        setTitle(
          wasRenamed ? data.title : (firstUserMessage?.content ?? data.title)
        );
        setMessages(
          (data.messages ?? []).map((item) => ({
            id: nextMessageId(),
            sender: item.role,
            text: item.content,
            citations: item.citations ?? [],
            status: 'done',
            messageId: item.id,
            feedback: item.feedback?.rating ?? null,
            feedbackComment: item.feedback?.comment ?? '',
          }))
        );
      })
      .catch((err) => console.log(err))
      .finally(() => setIsLoadingHistory(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handles the detection of multiline input
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;

    const checkMultiline = () => {
      const styles = window.getComputedStyle(el);
      const lineHeight = parseFloat(styles.lineHeight) || 20;
      const paddingY =
        (parseFloat(styles.paddingTop) || 0) +
        (parseFloat(styles.paddingBottom) || 0);
      const singleLineHeight = lineHeight + paddingY;
      setIsInputMultiline(el.scrollHeight > singleLineHeight + 4);
    };

    const observer = new ResizeObserver(checkMultiline);
    observer.observe(el);
    checkMultiline();
    return () => observer.disconnect();
  }, []);

  const handleSend = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || isSendingRef.current) return;

    isSendingRef.current = true;
    setIsSending(true);

    // Chat title is set to first user message
    if (messages.length === 0) setTitle(trimmed);

    setMessages((prev) => [
      ...prev,
      { id: nextMessageId(), sender: 'user', text: trimmed },
    ]);
    setInput('');

    const assistantId = nextMessageId();
    setMessages((prev) => [
      ...prev,
      {
        id: assistantId,
        sender: 'assistant',
        text: '',
        citations: [],
        status: 'streaming',
      },
    ]);

    // Track whether this request starts a new conversation for the sidebar.
    const startingNewConversation = conversationIdRef.current === null;

    try {
      const response = await fetch(apiUrl('/assistant/chat'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: trimmed,
          ...(conversationIdRef.current
            ? { conversation_id: conversationIdRef.current }
            : {}),
        }),
      });

      if (response.status === 401) {
        patchMessage(assistantId, (msg) =>
          markFailed(msg, SESSION_EXPIRED_MESSAGE)
        );
        return;
      }

      if (!response.ok || !response.body) {
        throw new Error(
          `Assistant request failed with status ${response.status}`
        );
      }

      await readAssistantStream(response, ({ type, data }) => {
        switch (type) {
          case 'start':
            conversationIdRef.current = data.conversation_id;
            setActiveConversationId(data.conversation_id);
            patchMessage(assistantId, (msg) => ({
              ...msg,
              messageId: data.message_id,
            }));
            break;
          case 'token':
            patchMessage(assistantId, (msg) => ({
              ...msg,
              text: msg.text + data.text,
            }));
            break;
          case 'citation':
            patchMessage(assistantId, (msg) => ({
              ...msg,
              citations: [...msg.citations, data],
            }));
            break;
          case 'escalation':
            patchMessage(assistantId, (msg) => ({
              ...msg,
              status: 'escalated',
              text: `I can't help with that here. Please contact ${data.contact_name} via ${data.contact_channel}.`,
            }));
            break;
          case 'done':
            patchMessage(assistantId, (msg) => ({ ...msg, status: 'done' }));
            if (startingNewConversation) {
              onConversationStarted?.(conversationIdRef.current);
            }
            break;
          case 'error':
            patchMessage(assistantId, (msg) => markFailed(msg));
            break;
          default:
            break;
        }
      });
    } catch (err) {
      console.log(err);
      patchMessage(assistantId, (msg) => markFailed(msg));
    } finally {
      isSendingRef.current = false;
      setIsSending(false);
    }
  };

  // Resets the chat panel to a new chat state, clearing messages and input.
  const handleNewChat = () => {
    conversationIdRef.current = null;
    setActiveConversationId(null);
    setIsRenameModalOpen(false);
    setMessages([]);
    setInput('');
    setTitle('New Chat');
    setPromptsExpanded(false);
    onNewChat?.();
  };

  const openRenameModal = () => {
    setRenameOpenCount((count) => count + 1);
    setIsRenameModalOpen(true);
  };

  const handleConfirmRename = async (newTitle) => {
    setIsRenameModalOpen(false);
    if (newTitle === title) return;

    const previousTitle = title;
    setTitle(newTitle);

    try {
      const response = await fetch(
        apiUrl(`/assistant/conversations/${activeConversationId}`),
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: newTitle }),
        }
      );
      if (!response.ok)
        throw new Error(`Failed to rename conversation: ${response.status}`);
      onConversationRenamed?.(activeConversationId, newTitle);
    } catch (err) {
      console.log(err);
      setTitle(previousTitle);
    }
  };

  // Opens chatbot page with history view.
  const handleHistoryClick = () => {
    if (onHistoryClick) {
      onHistoryClick();
    } else {
      navigate('/chatbot');
    }
  };

  // Handles Radial Gradient Mouse Glow on Prompt Chips
  const handleChipMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--x', `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty('--y', `${e.clientY - rect.top}px`);
  };

  // Renders the same chips in both layouts.
  const renderPromptChip = (prompt) => (
    <Chip
      key={prompt}
      label={prompt}
      variant="outlined"
      clickable
      onClick={() => handleSend(prompt)}
      onMouseMove={handleChipMouseMove}
      sx={promptChipSx}
    />
  );

  // Handles the state of whether the prompt row can scroll left/right, and adds a wheel listener for horizontal scrolling.
  const showPromptScroller = variant !== 'sidePanel' && messages.length === 0;

  const updatePromptScrollState = () => {
    const el = promptRowRef.current;
    if (!el) return;
    setCanScrollPromptsLeft(el.scrollLeft > 4);
    setCanScrollPromptsRight(
      el.scrollLeft + el.clientWidth < el.scrollWidth - 4
    );
  };

  useEffect(() => {
    if (!showPromptScroller) return;
    updatePromptScrollState();
    window.addEventListener('resize', updatePromptScrollState);
    return () => window.removeEventListener('resize', updatePromptScrollState);
  }, [showPromptScroller]);

  const scrollPrompts = (direction) => {
    promptRowRef.current?.scrollBy({
      left: direction * PROMPT_SCROLL_AMOUNT,
      behavior: 'smooth',
    });
  };

  // Adds a wheel listener for the horizontal scrolling.
  // Shift + scroll wheel.
  useEffect(() => {
    if (!showPromptScroller) return;
    const el = promptRowRef.current;
    if (!el) return;

    const handleWheel = (e) => {
      if (!e.shiftKey || e.deltaY === 0) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [showPromptScroller]);

  const displayTitle = truncateTitle(title);

  const sendButton = (
    <Tooltip title="Send message">
      <span>
        <IconButton
          aria-label="Send message"
          onClick={() => handleSend(input)}
          disabled={!input.trim() || isSending}
          size="small"
          sx={sendButtonSx(!!input.trim())}
        >
          <SendIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        flex: 1,
        minHeight: 0,
      }}
    >
      {/* Chat Title + Icons */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            minWidth: 0,
            flex: 1,
          }}
        >
          <Tooltip title={title}>
            <Typography
              variant="subtitle1"
              noWrap
              sx={{ color: '#FFFFFF', minWidth: 0 }}
            >
              {displayTitle}
            </Typography>
          </Tooltip>
          {activeConversationId && variant !== 'sidePanel' && (
            <Tooltip title="Rename chat">
              <IconButton
                size="small"
                aria-label="Rename chat"
                onClick={openRenameModal}
                sx={{ ...headerIconButtonSx, flexShrink: 0 }}
              >
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexShrink: 0 }}>
          {variant === 'sidePanel' && (
            <Tooltip title="Chat history">
              <IconButton
                size="small"
                aria-label="Chat history"
                onClick={handleHistoryClick}
                sx={headerIconButtonSx}
              >
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="New chat">
            <IconButton
              size="small"
              aria-label="New chat"
              onClick={handleNewChat}
              sx={headerIconButtonSx}
            >
              <AddBoxIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Chat Box */}
      <Box
        ref={chatBoxRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          py: 1,
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255, 255, 255, 0.25) transparent',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.25)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(255, 255, 255, 0.4)',
          },
        }}
      >
        {isLoadingHistory ? (
          <Box sx={{ m: 'auto' }}>
            <TypingIndicator />
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ m: 'auto', textAlign: 'center', px: 2 }}>
            <SmartToyIcon
              sx={{ fontSize: 32, color: '#FFFFFF', opacity: 0.5, mb: 1 }}
            />
            <Typography variant="body2" sx={{ color: '#FFFFFF', opacity: 0.6 }}>
              Ask me anything about your timesheet, leave, or account.
            </Typography>
          </Box>
        ) : (
          messages.map((msg) => (
            <Box key={msg.id} sx={messageRowSx(msg.sender)}>
              <Box sx={messageBubbleSx(msg.sender)}>
                {!msg.text && msg.status === 'streaming' ? (
                  <TypingIndicator />
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap' }}
                    color={msg.status === 'error' ? 'error.light' : undefined}
                  >
                    {msg.text}
                  </Typography>
                )}
                {msg.citations?.length > 0 && (
                  <Box sx={{ mt: 0.75, opacity: 0.7 }}>
                    <Typography variant="caption" component="div">
                      Sources: {uniqueSourceTitles(msg.citations).join(', ')}
                    </Typography>
                  </Box>
                )}
                {msg.sender === 'assistant' &&
                  msg.status === 'done' &&
                  msg.messageId && (
                    <Box sx={feedbackRowSx}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.25,
                        }}
                      >
                        <Tooltip title="Good response">
                          <IconButton
                            size="small"
                            aria-label="Good response"
                            onClick={() => handleSelectRating(msg, 'up')}
                            sx={feedbackIconSx(msg.feedback === 'up')}
                          >
                            {msg.feedback === 'up' ? (
                              <ThumbUpAltIcon sx={{ fontSize: 16 }} />
                            ) : (
                              <ThumbUpAltOutlinedIcon sx={{ fontSize: 16 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Bad response">
                          <IconButton
                            size="small"
                            aria-label="Bad response"
                            onClick={() => handleSelectRating(msg, 'down')}
                            sx={feedbackIconSx(msg.feedback === 'down')}
                          >
                            {msg.feedback === 'down' ? (
                              <ThumbDownAltIcon sx={{ fontSize: 16 }} />
                            ) : (
                              <ThumbDownAltOutlinedIcon sx={{ fontSize: 16 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                        {!msg.feedbackDraftOpen && (
                          <Chip
                            size="small"
                            clickable
                            variant="outlined"
                            label={
                              msg.feedbackComment
                                ? 'Edit comment'
                                : 'Add a comment'
                            }
                            onClick={() => handleOpenFeedbackComment(msg)}
                            onMouseMove={handleChipMouseMove}
                            sx={feedbackCommentChipSx}
                          />
                        )}
                        {!msg.feedbackDraftOpen &&
                          msg.feedbackStatus === 'saved' && (
                            <Typography variant="caption" sx={feedbackNoteSx()}>
                              {FEEDBACK_SAVED_MESSAGE}
                            </Typography>
                          )}
                      </Box>
                      <Collapse in={!!msg.feedbackDraftOpen}>
                        <Box
                          sx={{
                            mt: 0.75,
                            display: 'flex',
                            gap: 0.75,
                            alignItems: 'flex-end',
                          }}
                        >
                          <TextField
                            fullWidth
                            multiline
                            minRows={1}
                            maxRows={4}
                            variant="standard"
                            size="small"
                            placeholder="Tell us more (optional)"
                            value={msg.feedbackDraft ?? ''}
                            onChange={(e) =>
                              handleFeedbackDraftChange(msg.id, e.target.value)
                            }
                            InputProps={{ disableUnderline: true }}
                            inputProps={{
                              maxLength: FEEDBACK_COMMENT_MAX_LENGTH,
                              style: { color: '#FFFFFF' },
                            }}
                            sx={{
                              border: '1px solid rgba(255, 255, 255, 0.24)',
                              borderRadius: '8px',
                              px: 1,
                              py: 0.5,
                              transition: 'border-color 150ms ease',
                              '&:hover': {
                                borderColor: 'rgba(255, 255, 255, 0.4)',
                              },
                              '&:focus-within': { borderColor: '#84D8C5' },
                              '& .MuiInputBase-root': { alignItems: 'center' },
                              '& .MuiInputBase-input': { color: '#FFFFFF' },
                              '& .MuiInputBase-input::placeholder': {
                                color: 'rgba(255, 255, 255, 0.6)',
                                opacity: 1,
                              },
                            }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={
                              !msg.feedback || msg.feedbackStatus === 'saving'
                            }
                            onClick={() => handleSubmitFeedback(msg)}
                            sx={{
                              flexShrink: 0,
                              textTransform: 'none',
                              color: '#84D8C5',
                              borderColor: 'rgba(132, 216, 197, 0.5)',
                              '&:hover': {
                                borderColor: '#84D8C5',
                                bgcolor: 'transparent',
                                color: '#ffffff',
                              },
                              '&.Mui-disabled': {
                                color: 'rgba(255, 255, 255, 0.3)',
                                borderColor: 'rgba(255, 255, 255, 0.15)',
                              },
                            }}
                          >
                            {msg.feedbackStatus === 'saving'
                              ? 'Sending…'
                              : 'Submit'}
                          </Button>
                        </Box>
                        <Typography
                          variant="caption"
                          component="div"
                          sx={{
                            textAlign: 'right',
                            mt: 0.25,
                            color:
                              (msg.feedbackDraft ?? '').length >=
                              FEEDBACK_COMMENT_MAX_LENGTH
                                ? 'error.light'
                                : 'rgba(255, 255, 255, 0.4)',
                          }}
                        >
                          {(msg.feedbackDraft ?? '').length}/
                          {FEEDBACK_COMMENT_MAX_LENGTH}
                        </Typography>
                        {msg.feedbackStatus === 'error' && (
                          <Typography
                            variant="caption"
                            sx={{
                              ...feedbackNoteSx(true),
                              display: 'block',
                              mt: 0.5,
                            }}
                          >
                            {FEEDBACK_ERROR_MESSAGE}
                          </Typography>
                        )}
                        {msg.feedbackStatus === 'unauthorized' && (
                          <Typography
                            variant="caption"
                            sx={{
                              ...feedbackNoteSx(true),
                              display: 'block',
                              mt: 0.5,
                            }}
                          >
                            {FEEDBACK_SESSION_EXPIRED_MESSAGE}
                          </Typography>
                        )}
                      </Collapse>
                    </Box>
                  )}
              </Box>
            </Box>
          ))
        )}
      </Box>

      {/* Side panel: collapsible 2-per-row grid */}
      {messages.length === 0 && variant === 'sidePanel' && (
        <Box sx={{ py: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', pb: 0.5 }}>
            <Tooltip
              title={
                promptsExpanded ? 'Show fewer prompts' : 'Show more prompts'
              }
            >
              <IconButton
                size="small"
                aria-label={
                  promptsExpanded ? 'Show fewer prompts' : 'Show more prompts'
                }
                onClick={() => setPromptsExpanded((expanded) => !expanded)}
                sx={promptScrollArrowSx}
              >
                <ExpandLessIcon
                  fontSize="small"
                  sx={promptToggleIconSx(promptsExpanded)}
                />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            {SUGGESTED_PROMPTS.slice(0, PROMPT_COLLAPSED_COUNT).map(
              renderPromptChip
            )}
          </Box>
          <Collapse in={promptsExpanded}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 1,
                pt: 1,
              }}
            >
              {SUGGESTED_PROMPTS.slice(PROMPT_COLLAPSED_COUNT).map(
                renderPromptChip
              )}
            </Box>
          </Collapse>
        </Box>
      )}

      {showPromptScroller && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 1 }}>
          <Tooltip title="Scroll left">
            <span>
              <IconButton
                aria-label="Scroll prompts left"
                size="small"
                disabled={!canScrollPromptsLeft}
                onClick={() => scrollPrompts(-1)}
                sx={promptScrollArrowSx}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Box
            ref={promptRowRef}
            onScroll={updatePromptScrollState}
            sx={{
              display: 'flex',
              gap: 1,
              flex: 1,
              minWidth: 0,
              overflowX: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {SUGGESTED_PROMPTS.map(renderPromptChip)}
          </Box>

          <Tooltip title="Scroll right">
            <span>
              <IconButton
                aria-label="Scroll prompts right"
                size="small"
                disabled={!canScrollPromptsRight}
                onClick={() => scrollPrompts(1)}
                sx={promptScrollArrowSx}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}

      {/* Message Input */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          mt: 1,
          px: 1.5,
          py: 0.75,
          borderRadius: isInputMultiline ? '16px' : '20px',
          border: '1px solid rgba(255, 255, 255, 0.24)',
          bgcolor: 'rgba(255, 255, 255, 0.06)',
          transition: 'border-radius 150ms ease, border-color 150ms ease',
          '&:hover': { borderColor: 'rgba(255, 255, 255, 0.4)' },
          '&:focus-within': { borderColor: '#84D8C5' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: isInputMultiline ? 'flex-start' : 'center',
            gap: 1,
          }}
        >
          <TextField
            fullWidth
            multiline
            minRows={1}
            maxRows={6}
            variant="standard"
            size="small"
            placeholder="Message EPS Assistant"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend(input);
              }
            }}
            InputProps={{
              disableUnderline: true,
              inputRef: textareaRef,
            }}
            sx={{
              '& .MuiInputBase-input': {
                color: '#FFFFFF',
                marginTop: '4px',
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(255, 255, 255, 0.25) transparent',
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(255, 255, 255, 0.25)',
                  borderRadius: '3px',
                },
              },
              '& .MuiInputBase-input::placeholder': {
                color: 'rgba(255, 255, 255, 0.5)',
                opacity: 1,
              },
            }}
          />
          {!isInputMultiline && sendButton}
        </Box>

        {isInputMultiline && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 0.5 }}>
            {sendButton}
          </Box>
        )}
      </Box>
      {variant !== 'sidePanel' && (
        <EditTextModal
          key={renameOpenCount}
          open={isRenameModalOpen}
          onCancel={() => setIsRenameModalOpen(false)}
          title="Rename Chat"
          initialText={title}
          maxLength={120}
          confirmLabel="Save"
          onConfirm={handleConfirmRename}
        />
      )}
    </Box>
  );
};

ChatPanel.propTypes = {
  variant: PropTypes.string,
  onNewChat: PropTypes.func,
  onHistoryClick: PropTypes.func,
  onConversationStarted: PropTypes.func,
  onConversationRenamed: PropTypes.func,
  initialTitle: PropTypes.string,
  conversationId: PropTypes.string,
};
